import { generateRadioButtonOptions } from 'components/final-form/radioUtils';
import React, { Component } from "react";
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Field, FieldProps, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { Button, Grid, RadioProps } from 'semantic-ui-react';
import { UpsertVatDto } from 'ts-types/api.types';
import { composeValidators, maxChars, mustBeNumber, mustBePositiveNumber, required } from 'utils/validatorUtils';
import Input from 'components/final-form/Input';
import RadioGroup from 'components/final-form/RadioGroup';
import CheckBox from 'components/final-form/CheckBox';
import { addVatGroup, editVatGroup } from 'services/vatGroupServices';
import axios, { CancelTokenSource } from 'axios';
import { numberFormatter } from 'utils/fomattingUtils';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';

interface Props extends MessageSourceProps {
  onCancel: () => void,
  onSave: () => void,
  vatGroupId?: number,
  vatGroup?: UpsertVatDto,
}

interface State {
  vatGroup: Partial<UpsertVatDto>,
  errorMessage?: string,
  types: Array<RadioProps>,
  cancelTokenSource: CancelTokenSource
}

const VALIDATED_FIELDS: Array<string> = [
  "code", "description", "value"
];

const initialVatGroup: Partial<UpsertVatDto> = {
  description: "",
  code: "",
  basicGroup: false,
  type: 0
};

class UpsertVatGroupForm extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {getMessage, vatGroup = initialVatGroup} = this.props;

    const cancelTokenSource = axios.CancelToken.source();

    const typeLabels: Array<string> = ["incl", "excl"];

    this.state = {
      vatGroup: vatGroup,
      types: generateRadioButtonOptions(typeLabels, "vat.group.type", getMessage),
      cancelTokenSource
    };
  };

  handleSubmit = async (values: Partial<UpsertVatDto>) => {

    const {vatGroupId, getMessage, onSave} = this.props;
    const {cancelTokenSource} = this.state;

    const handleError = () => {
      this.setState({
        errorMessage: getMessage("error.general")
      })
    };

    if (vatGroupId) {
      await editVatGroup(vatGroupId, values, cancelTokenSource)
          .then(onSave)
          .catch(handleError)
    } else {
      await addVatGroup(values, cancelTokenSource)
          .then(onSave)
          .catch(handleError)
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  render(): React.ReactNode {

    const {vatGroup} = this.state;

    return (
        <FinalForm
            onSubmit={(values) => this.handleSubmit(values)}
            initialValues={{
              ...vatGroup,
              value: vatGroup.value ? numberFormatter.format(vatGroup.value) : undefined
            }}
            subscription={{values: true, pristine: true, submitting: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({handleSubmit, submitting}: FormRenderProps): React.ReactNode => {

    const {getMessage, onCancel} = this.props;
    const {errorMessage} = this.state;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              {errorMessage
              && (
                  <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                    {errorMessage}
                  </StyledErrorMessage>
              )
              }
              <PraxFormGrid>
                {this.renderBasicInformationSection()}
              </PraxFormGrid>
            </FormContent>

            <FormButtonsRow>
              <Button compact type="submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel} >{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  };

  renderBasicInformationSection = (): React.ReactNode => {

    const {types} = this.state;
    const {getMessage} = this.props;

    const definitionOfVatGroup: Array<Array<string>> = [
      ["description"],
      ["code"],
      ["value"]
    ];

    const validator = (value: string) => required(value);
    const codeValidator = composeValidators(maxChars(10), required);
    const descriptionSection = definitionOfVatGroup.map((row, index) => {
      return (
          <Grid.Row key={index}>
            {
              row.map((field, index1) => {

                let fieldProps: FieldProps = {
                  name: field,
                  validate: VALIDATED_FIELDS.includes(field) ? field === "code" ? codeValidator : validator : undefined,
                  component: Input,
                  icon: (field === "value" ? "percent" : undefined)
                };

                if(field === "value") {
                  fieldProps = {
                    name: field,
                    validate: composeValidators(mustBeNumber, mustBePositiveNumber),
                    component: Input,
                    icon: (field === "value" ? "percent" : undefined)
                  };
                }


                return (
                    <React.Fragment key={index1}>
                      <Grid.Column width={5} verticalAlign="middle">
                        {`${getMessage(field)}:`}
                      </Grid.Column>
                      <Grid.Column width={fieldProps.name === "code" ? 5 : 11} verticalAlign="middle">
                        <Field
                            fluid
                            {...fieldProps}
                        />
                      </Grid.Column>
                    </React.Fragment>
                );
              })
            }
          </Grid.Row>
      );
    });

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
              {`${getMessage("vat.group.type")}:`}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  name="type"
                  component={RadioGroup}
                  radioDefinitions={types}
                  radiowidth="33%"
              />
            </Grid.Column>
          </Grid.Row>
          {descriptionSection}
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  name="basicGroup"
                  component={CheckBox}
                  label={getMessage(`vat.group.basicGroup`)}
              />
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    );
  };

}

export default withMessages(UpsertVatGroupForm);
